import { observer } from "mobx-react";
import React from "react";
import {
  Stack,
  Modal,
  Button,
  Group,
  rem,
  Indicator,
  useMantineTheme,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { Cubby } from "../stores/Cubby";
import { MODALS } from "../helpers/constants";
import {
  IconArrowBigDownFilled,
  IconArrowBigUpFilled,
  IconBolt,
  IconBoltFilled,
  IconExchange,
  IconHeart,
  IconHistory,
  IconPlugConnected,
  IconRocket,
  IconUsers,
} from "@tabler/icons-react";

const SomeButtons = observer(({ cubby }: { cubby: Cubby }) => {
  const theme = useMantineTheme();

  const makeSendLightningToolTip = () => {
    if (cubby.isConsideredReadyForBasicLightningApiRequests) {
      return "You need a fully open channel with outbound capacity to send payments.";
    } else if (cubby.runStatus == "stopped") {
      return "Start the node to send payments.";
    } else {
      return "Node is syncing, please wait.";
    }
  };

  const makeReceiveLightningToolTip = () => {
    if (cubby.isConsideredReadyForBasicLightningApiRequests) {
      return "You need a fully open channel with inbound capacity to receive payments.";
    } else if (cubby.runStatus == "stopped") {
      return "Start the node to receive payments.";
    }
    return "Node is syncing, please wait.";
  };

  const makeDepositBtcToolTip = () => {
    if (cubby.runStatus == "stopped") {
      return "Start the node to deposit BTC.";
    }
    return cubby.allowOnchainActions ? "" : "Node is syncing, please wait.";
  };

  const makeWithdrawBtcToolTip = () => {
    if (!cubby.allowOnchainActions) {
      return "Start the node to withdraw BTC.";
    } else if (cubby.totalOfAllBalances < 1) {
      return "You need funds to withdraw BTC.";
    }
    return "";
  };

  const makeGetInboundToolTip = () => {
    if (cubby.runStatus == "stopped") {
      return "Start the node to get inbound liquidity.";
    } else if (!cubby.isConsideredReadyForBasicLightningApiRequests) {
      return "Node is not ready for inbound liquidity.";
    }

    return cubby.lspPublicKeyIfConnectedOrNull == null
      ? "Refresh this page to connect to the LSP."
      : "";
  };

  const makeOpenChannelToolTip = () => {
    return cubby.isConsideredReadyForBasicLightningApiRequests
      ? ""
      : "Node is not ready for opening channels.";
  };

  const makeAddPeerToolTip = () => {
    return cubby.isConsideredReadyForBasicLightningApiRequests
      ? ""
      : "Node is not ready for adding peers.";
  };

  const makePeersToolTip = () => {
    return cubby.isConsideredReadyForBasicLightningApiRequests
      ? ""
      : "Node is not ready to show peers.";
  };

  return (
    <>
      <Group>
        <Tooltip
          multiline
          w={300}
          disabled={
            cubby.lspPublicKeyIfConnectedOrNull != null &&
            cubby.isConsideredReadyForBasicLightningApiRequests
          }
          withArrow
          transitionProps={{ duration: 200 }}
          label={makeGetInboundToolTip()}
        >
          <Button
            leftSection={<IconRocket />}
            size="sm"
            aria-label="get inbound"
            variant="default"
            disabled={
              cubby.lspPublicKeyIfConnectedOrNull == null ||
              !cubby.isConsideredReadyForBasicLightningApiRequests
            }
            onClick={() => cubby.setModal(MODALS.GET_INBOUND)}
          >
            Get Inbound
          </Button>
        </Tooltip>

        <Tooltip
          multiline
          w={300}
          disabled={
            cubby.isConsideredReadyForBasicLightningApiRequests &&
            cubby.outboundLiquidity > 0
          }
          withArrow
          transitionProps={{ duration: 200 }}
          label={makeSendLightningToolTip()}
        >
          <Button
            leftSection={<IconBoltFilled />}
            size="sm"
            aria-label="send lightning"
            variant="default"
            disabled={
              !cubby.isConsideredReadyForBasicLightningApiRequests ||
              cubby.outboundLiquidity < 1
            }
            onClick={() => cubby.setModal(MODALS.SEND_LIGHTNING)}
          >
            Send Lightning
          </Button>
        </Tooltip>

        <Tooltip
          multiline
          w={300}
          disabled={
            cubby.isConsideredReadyForBasicLightningApiRequests &&
            cubby.inboundLiquidity > 0
          }
          withArrow
          transitionProps={{ duration: 200 }}
          label={makeReceiveLightningToolTip()}
        >
          <Button
            leftSection={<IconBolt />}
            size="sm"
            aria-label="receive lightning"
            variant="default"
            disabled={!cubby.canReceiveLightningPayments}
            onClick={() => cubby.setModal(MODALS.RECEIVE_LIGHTNING)}
          >
            Receive Lightning
          </Button>
        </Tooltip>

        <Tooltip
          multiline
          w={300}
          disabled={cubby.allowOnchainActions}
          withArrow
          transitionProps={{ duration: 200 }}
          label={makeDepositBtcToolTip()}
        >
          <Button
            leftSection={<IconArrowBigDownFilled />}
            size="sm"
            aria-label="deposit btc"
            variant="default"
            disabled={!cubby.allowOnchainActions}
            onClick={() => cubby.setModal(MODALS.DEPOSIT_BTC)}
          >
            Deposit BTC
          </Button>
        </Tooltip>

        <Tooltip
          multiline
          w={300}
          disabled={cubby.allowOnchainActions && cubby.totalOfAllBalances >= 1}
          withArrow
          transitionProps={{ duration: 200 }}
          label={makeWithdrawBtcToolTip()}
        >
          <Button
            leftSection={<IconArrowBigUpFilled />}
            size="sm"
            aria-label="withdraw btc"
            variant="default"
            disabled={
              !cubby.allowOnchainActions || cubby.totalOfAllBalances < 1
            }
            onClick={() => cubby.setModal(MODALS.SEND_BTC)}
          >
            Withdraw BTC
          </Button>
        </Tooltip>
        <Tooltip
          multiline
          w={300}
          disabled={cubby.isConsideredReadyForBasicLightningApiRequests}
          withArrow
          transitionProps={{ duration: 200 }}
          label={makeOpenChannelToolTip()}
        >
          <Button
            leftSection={<IconExchange />}
            size="sm"
            aria-label="open channel to peer"
            variant="default"
            disabled={!cubby.isConsideredReadyForBasicLightningApiRequests}
            onClick={() => cubby.setModal(MODALS.OPEN_CHANNEL_TO_OTHER_NODE)}
          >
            Open Channel
          </Button>
        </Tooltip>

        <Tooltip
          multiline
          w={300}
          disabled={cubby.isConsideredReadyForBasicLightningApiRequests}
          withArrow
          transitionProps={{ duration: 200 }}
          label={makeAddPeerToolTip()}
        >
          <Button
            leftSection={<IconPlugConnected />}
            size="sm"
            aria-label="add peer"
            variant="default"
            disabled={!cubby.isConsideredReadyForBasicLightningApiRequests}
            onClick={() => cubby.setModal(MODALS.CONNECT_TO_OTHER_NODE)}
          >
            Add Peer
          </Button>
        </Tooltip>

        <Tooltip
          multiline
          w={300}
          disabled={cubby.isConsideredReadyForBasicLightningApiRequests}
          withArrow
          transitionProps={{ duration: 200 }}
          label={makePeersToolTip()}
        >
          <Indicator
            label={cubby.peerLists.data?.length}
            size={16}
            disabled={cubby.shouldShowPeerNumberIndicator == false}
            color={theme.primaryColor}
          >
            <Button
              size="sm"
              leftSection={<IconUsers />}
              onClick={() => {
                cubby.setShouldShowPeerNumberIndicator(false);
                cubby.cubbyLife("get_peers");
                cubby.setModal(MODALS.SHOW_PEERS);
              }}
              disabled={!cubby.isConsideredReadyForBasicLightningApiRequests}
              variant="default"
            >
              Peers
            </Button>
          </Indicator>
        </Tooltip>
      </Group>

      <Group></Group>
    </>
  );
});

export default SomeButtons;
